// Phase P.12 — Calendar Month view (redesigned).
// 6 rows × 7 columns (Mon → Sun). Each day cell shows:
//   - Day number (top-left, muted for prev/next month days)
//   - Job count badge (top-right)
//   - First 2 jobs as compact one-liners: "10:00 · Alpha Co"
//   - "+N more" link when >2 jobs
// Click a cell → jump to Day view for that date (parent handler).
// Click a job pill → open the BookingDetail sheet.

import { useMemo } from "react";
import { ChevronLeft, ChevronRight, Ban, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { paymentTintClasses, fmtHm, fmtHmRange } from "@/lib/calendar";

function ymdKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function CalendarMonthView({
  monthGridDays,
  monthAnchor,
  bookings,
  onBookingClick,
  onJumpToDay,
  onEmptyCellClick,
  todayYmd,
  monthLabel,
  onPrev,
  onNext,
  onToday,
  // Phase P.27 — optional day-block + note surfaces.
  blocksByDate,
  notesByDate,
  onBlockDay,
}) {
  const byDay = useMemo(() => {
    const map = new Map();
    for (const b of bookings || []) {
      if (!b?.scheduled_start) continue;
      const d = new Date(b.scheduled_start);
      if (Number.isNaN(d.getTime())) continue;
      const key = ymdKey(d);
      const arr = map.get(key) || [];
      arr.push(b);
      map.set(key, arr);
    }
    for (const [, arr] of map) {
      arr.sort((a, b) => (a.scheduled_start || "").localeCompare(b.scheduled_start || ""));
    }
    return map;
  }, [bookings]);

  return (
    <div className="bg-card border border-border rounded-[10px] overflow-hidden" data-testid="calendar-month-view">
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-secondary/40">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={onPrev} data-testid="month-prev" aria-label="Previous month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onToday} data-testid="month-today">Today</Button>
          <Button variant="outline" size="icon" onClick={onNext} data-testid="month-next" aria-label="Next month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="text-lg font-black tracking-tight" data-testid="month-header">{monthLabel}</div>
        <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground min-w-[70px] text-right">
          {(bookings || []).length} {(bookings || []).length === 1 ? "job" : "jobs"}
        </div>
      </header>

      {/* weekday header row */}
      <div className="grid grid-cols-7 border-b border-border bg-secondary/20">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((w) => (
          <div
            key={w}
            className="px-2 py-2 text-center mono text-[11px] uppercase tracking-widest text-muted-foreground border-l border-border first:border-l-0"
          >
            {w}
          </div>
        ))}
      </div>

      {/* 6x7 grid */}
      <div className="grid grid-cols-7 auto-rows-fr" style={{ minHeight: 620 }}>
        {monthGridDays.map((d, idx) => {
          const key = ymdKey(d);
          const inMonth = d.getMonth() === monthAnchor.month;
          const isToday = key === todayYmd;
          const items = byDay.get(key) || [];
          const shown = items.slice(0, 2);
          const extra = items.length - shown.length;
          // Phase P.27 — block + notes markers on this cell.
          const block = blocksByDate ? blocksByDate.get(key) : null;
          const dayNotes = notesByDate ? (notesByDate.get(key) || []) : [];
          return (
            <button
              key={key + idx}
              type="button"
              onClick={(e) => {
                if (e.target?.closest?.("[data-booking-pill]")) return;
                if (items.length > 0) {
                  onJumpToDay?.(d);
                } else {
                  onEmptyCellClick?.(d);
                }
              }}
              className={`text-left border-l border-t border-border first:border-l-0 hover:bg-secondary/30 transition-colors flex flex-col gap-1.5 px-2 py-2 min-h-[110px] ${
                inMonth ? "bg-card" : "bg-muted/40"
              } ${isToday ? "ring-1 ring-inset ring-accent" : ""} ${block ? "bg-destructive/[0.05]" : ""}`}
              data-testid={`month-cell-${key}`}
              data-in-month={inMonth ? "1" : "0"}
              data-today={isToday ? "1" : "0"}
            >
              <div className="flex items-start justify-between gap-1">
                <span
                  className={`text-sm font-extrabold ${
                    inMonth ? (isToday ? "text-accent" : "") : "text-muted-foreground/50"
                  } ${block ? "text-destructive" : ""}`}
                >
                  {d.getDate()}
                </span>
                <div className="flex items-center gap-1">
                  {onBlockDay && inMonth && (
                    <span
                      role="button"
                      tabIndex={0}
                      data-booking-pill
                      onClick={(e) => { e.stopPropagation(); onBlockDay(d); }}
                      className={`inline-flex items-center justify-center rounded-full h-4 w-4 ${block ? "text-destructive bg-destructive/10 hover:bg-destructive/20" : "text-muted-foreground/60 hover:bg-secondary/80 hover:text-foreground"}`}
                      title={block ? `Blocked: ${block.reason || "day off"} (click to edit)` : "Block this day"}
                      data-testid={`month-block-btn-${key}`}
                    >
                      <Ban className="h-3 w-3" />
                    </span>
                  )}
                  {dayNotes.length > 0 && (
                    <span
                      className="inline-flex items-center justify-center rounded-full h-4 px-1 text-amber-800 bg-amber-100 border border-amber-200"
                      title={dayNotes.map((n) => `${fmtHm(new Date(n.at))} · ${n.note}`).join("\n")}
                      data-testid={`month-notes-badge-${key}`}
                    >
                      <StickyNote className="h-2.5 w-2.5 mr-0.5" />
                      <span className="text-[9px] mono font-bold">{dayNotes.length}</span>
                    </span>
                  )}
                  {items.length > 0 && (
                    <span
                      className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-secondary text-foreground/80"
                      data-testid={`month-cell-${key}-count`}
                    >
                      {items.length} {items.length === 1 ? "job" : "jobs"}
                    </span>
                  )}
                </div>
              </div>
              {block && (
                <div className="mono text-[9px] uppercase tracking-widest text-destructive truncate" data-testid={`month-block-badge-${key}`}>
                  Blocked: {block.reason || "day off"}
                </div>
              )}
              <div className="flex-1 flex flex-col gap-1 overflow-hidden">
                {shown.map((b) => {
                  const tint = paymentTintClasses(b);
                  const multi = Array.isArray(b.template_ids) && b.template_ids.length > 1;
                  return (
                    <span
                      key={b.id}
                      role="button"
                      tabIndex={0}
                      data-booking-pill
                      data-testid={`month-booking-${b.id}`}
                      onClick={(e) => { e.stopPropagation(); onBookingClick?.(b.id); }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onBookingClick?.(b.id);
                        }
                      }}
                      className={`truncate text-[11px] rounded px-1.5 py-0.5 border-l-2 ${tint.border} ${tint.bg} hover:shadow-sm cursor-pointer`}
                      title={`${b.customer_name || ""} · ${fmtHmRange(b)}${multi ? ` · ${b.template_ids.length} services` : ""}`}
                    >
                      <span className="mono font-bold mr-1">{fmtHm(b.scheduled_start)}</span>
                      <span className="truncate">{b.customer_name || "—"}</span>
                      {multi && (
                        <span
                          className="ml-1 mono text-[9px] font-bold text-amber-800 bg-amber-100 border border-amber-200 rounded px-1"
                          data-testid={`booking-multi-template-badge-${b.id}`}
                        >
                          +{b.template_ids.length - 1}⚠
                        </span>
                      )}
                    </span>
                  );
                })}
                {extra > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onJumpToDay?.(d); }}
                    className="text-[10px] font-bold text-accent hover:underline text-left"
                    data-testid={`month-cell-${key}-more`}
                  >
                    +{extra} more
                  </button>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
